/**
 * "Why this number?" popover.
 *
 * The product bar promises "every default visible, every assumption editable,
 * every result explained in one sentence." This helper is the "explained"
 * half for the *computed* outputs — break-even year, first-month outlay, chart
 * curves — where the one-sentence summary isn't enough to defend the number
 * to a spreadsheet-loving friend.
 *
 * Each popover states, in order:
 *   1. the plain-English formula that produced the number,
 *   2. every input that fed the formula, with the exact value used,
 *   3. the resulting number.
 *
 * Uses a native `<details>` disclosure so it degrades to a working expander
 * with no JS, works identically on touch and pointer, and is accessible by
 * default. `variant: "dark"` swaps the palette when the popover sits on the
 * navy answer card.
 */

export interface WhyNumberInput {
  readonly label: string;
  readonly value: string;
}

export interface WhyNumberResult {
  readonly label: string;
  readonly value: string;
}

export interface WhyNumberSpec {
  readonly formulaTitle: string;
  readonly formulaLines: readonly string[];
  readonly inputs: readonly WhyNumberInput[];
  readonly result: WhyNumberResult;
}

export interface WhyNumberHandle {
  readonly root: HTMLElement;
  update(spec: WhyNumberSpec): void;
}

export type WhyNumberVariant = "light" | "dark";

/**
 * Create a "Why this number?" popover mount. Returns a persistent DOM node
 * and an `update()` method the caller invokes on each recompute — this lets
 * the results view pin the disclosure state (open/closed) across state
 * changes instead of remounting the popover on every keystroke.
 */
export function createWhyNumber(variant: WhyNumberVariant = "light"): WhyNumberHandle {
  const details = document.createElement("details");
  details.className =
    variant === "dark" ? "why-number why-number--dark" : "why-number";
  const summary = document.createElement("summary");
  summary.className = "why-number-summary";
  summary.textContent = "Why this number?";
  details.append(summary);

  const body = document.createElement("div");
  body.className = "why-number-body";
  details.append(body);

  return {
    root: details,
    update(spec: WhyNumberSpec) {
      body.replaceChildren(...renderBody(spec));
    },
  };
}

function renderBody(spec: WhyNumberSpec): HTMLElement[] {
  return [
    renderFormula(spec.formulaTitle, spec.formulaLines),
    renderInputs(spec.inputs),
    renderAnswer(spec.result),
  ];
}

function renderFormula(title: string, lines: readonly string[]): HTMLElement {
  const block = document.createElement("div");
  block.className = "why-number-section";
  const heading = document.createElement("h4");
  heading.textContent = title;
  block.append(heading);
  for (const line of lines) {
    const p = document.createElement("p");
    p.className = "why-number-formula";
    p.textContent = line;
    block.append(p);
  }
  return block;
}

function renderInputs(inputs: readonly WhyNumberInput[]): HTMLElement {
  const block = document.createElement("div");
  block.className = "why-number-section";
  const heading = document.createElement("h4");
  heading.textContent = "Inputs";
  block.append(heading);
  const list = document.createElement("dl");
  list.className = "why-number-inputs";
  for (const item of inputs) {
    const dt = document.createElement("dt");
    dt.textContent = item.label;
    const dd = document.createElement("dd");
    dd.textContent = item.value;
    list.append(dt, dd);
  }
  block.append(list);
  return block;
}

function renderAnswer(result: WhyNumberResult): HTMLElement {
  const block = document.createElement("div");
  block.className = "why-number-section why-number-answer";
  const label = document.createElement("span");
  label.className = "why-number-answer-label";
  label.textContent = result.label;
  const value = document.createElement("strong");
  value.className = "why-number-answer-value";
  value.textContent = result.value;
  block.append(label, value);
  return block;
}
